import { Injectable } from '@angular/core';

/**
 * Claves canónicas de almacenamiento. Centralizar reduce errores de tipeo
 * y facilita auditar qué se persiste y dónde.
 */
export const STORAGE_KEYS = {
  token: 'accessToken',
  expiresAt: 'expires_at',
  user: 'user',
  workflows: 'workflows',
  statuses: 'statuses',
  clients: 'clients',
  priorities: 'priorities',
  preferences: 'preferences',
  rememberedEmail: 'remembered_email',
  sessionPassword: 'session_password'
} as const;

/** Datos sensibles -> sessionStorage (se pierde al cerrar pestaña). */
const SENSITIVE_KEYS: Set<string> = new Set([
  STORAGE_KEYS.token,
  STORAGE_KEYS.expiresAt,
  STORAGE_KEYS.user,
  STORAGE_KEYS.sessionPassword
]);

/** Claves de conveniencia que sobreviven al logout intencionadamente. */
const PERSIST_ON_LOGOUT: Set<string> = new Set([
  STORAGE_KEYS.rememberedEmail,
  STORAGE_KEYS.sessionPassword
]);

/**
 * Wrapper seguro alrededor de Web Storage:
 *  - separa datos sensibles (sessionStorage) de datos cacheables (localStorage)
 *  - tolera entornos sin storage (SSR, modo privado restrictivo)
 *  - parsea JSON con try/catch para que un valor corrupto no rompa la app
 */
@Injectable({ providedIn: 'root' })
export class StorageService {
  private get sensitive(): Storage | null {
    try { return typeof window !== 'undefined' ? window.sessionStorage : null; } catch { return null; }
  }

  private get persistent(): Storage | null {
    try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
  }

  private store(key: string): Storage | null {
    return SENSITIVE_KEYS.has(key) ? this.sensitive : this.persistent;
  }

  get(key: string): string | null {
    return this.store(key)?.getItem(key) ?? null;
  }

  set(key: string, value: string): void {
    try { this.store(key)?.setItem(key, value); } catch { /* quota exceeded, ignorar */ }
  }

  remove(key: string): void {
    try { this.store(key)?.removeItem(key); } catch { /* noop */ }
  }

  getJSON<T>(key: string, fallback: T): T {
    const raw = this.get(key);
    if (!raw) return fallback;
    try { return JSON.parse(raw) as T; } catch { return fallback; }
  }

  setJSON(key: string, value: unknown): void {
    try { this.set(key, JSON.stringify(value)); } catch { /* noop */ }
  }

  /** Limpia las claves de sesión. Conserva las claves de conveniencia (email recordado, contraseña de sesión). */
  clearAll(): void {
    Object.values(STORAGE_KEYS)
      .filter(k => !PERSIST_ON_LOGOUT.has(k))
      .forEach(k => this.remove(k));
  }
}
